import React, { useState } from 'react';
import axios from 'axios';
import { Upload, FileText, Download, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BulkUpload() {
  const [uploadType, setUploadType] = useState('positions');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a file'); return; }
    setUploading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/${uploadType}/bulk-upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setResult(response.data);
      if (response.data.created > 0) toast.success(`Successfully created ${response.data.created} ${uploadType}`);
      if (response.data.errors?.length > 0) toast.error(`${response.data.errors.length} rows had errors`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/templates/${uploadType}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${uploadType}_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="bulk-upload-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Bulk Upload</h1>
        <p className="text-slate-600">Upload positions, resumes, or candidate databases in bulk via CSV</p>
      </div>

      <div className="bg-white border border-blue-100 rounded shadow-sm p-6 max-w-2xl">
        <div className="mb-6">
          <label className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-2 block">Upload Type</label>
          <div className="flex flex-wrap gap-3">
            <button data-testid="type-positions-btn" onClick={() => { setUploadType('positions'); setFile(null); setResult(null); }}
              className={`px-4 py-2 rounded font-bold text-sm transition-colors ${uploadType === 'positions' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              Positions
            </button>
            <button data-testid="type-resumes-btn" onClick={() => { setUploadType('resumes'); setFile(null); setResult(null); }}
              className={`px-4 py-2 rounded font-bold text-sm transition-colors ${uploadType === 'resumes' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              Resumes
            </button>
          </div>
        </div>

        <div className="mb-6">
          <Button data-testid="download-template-btn" onClick={downloadTemplate} variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50 mb-4">
            <Download size={16} className="mr-2" />Download {uploadType} CSV Template
          </Button>
          <div className="border-2 border-dashed border-blue-200 rounded p-8 text-center bg-blue-50/50">
            <Upload size={36} className="mx-auto mb-3 text-blue-400" />
            <p className="text-sm text-slate-600 mb-3">Select a CSV file to upload</p>
            <input data-testid="file-input" type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])}
              className="text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-bold file:bg-blue-600 file:text-white hover:file:bg-blue-700" />
            {file && <p className="text-sm text-blue-700 mt-3 font-semibold"><FileText size={14} className="inline mr-1" />{file.name}</p>}
          </div>
        </div>

        <Button data-testid="upload-btn" onClick={handleUpload} disabled={!file || uploading} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold disabled:opacity-50">
          {uploading ? 'Uploading...' : `Upload ${uploadType}`}
        </Button>

        {result && (
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded">
              <CheckCircle2 size={18} className="text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-800" data-testid="upload-success-count">Successfully created: {result.created}</span>
            </div>
            {result.errors?.length > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle size={18} className="text-red-600" />
                  <span className="text-sm font-semibold text-red-800" data-testid="upload-error-count">Errors: {result.errors.length}</span>
                </div>
                <ul className="text-xs text-red-700 space-y-1 max-h-40 overflow-y-auto">
                  {result.errors.map((err) => <li key={err}>{err}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* TalentVault Import Note */}
      <div className="mt-6 bg-amber-50 border border-amber-200 rounded shadow-sm p-5 max-w-2xl" data-testid="talentvault-import-note">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-bold text-amber-900 text-sm mb-1">Migrating your candidate database?</p>
            <p className="text-xs text-amber-800 leading-relaxed mb-2">
              To bulk import your existing candidate data from another system, go to <strong>TalentVault</strong> and use the <strong>Import</strong> feature. It accepts CSV files with candidate details (name, phone, email, position, skills, CTC, etc.) and automatically deduplicates entries.
            </p>
            <Button variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-100 text-xs"
              onClick={() => window.location.href = '/talent-vault'}>
              Go to TalentVault Import
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
